import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { EventCard } from './EventCard';
import { useUserRsvpsForEvents } from '@/lib/hooks/useRsvps';
import { eventKeys } from '@/lib/hooks/useEvents';
import { useEventFileCounts } from '@/lib/hooks/useEventFiles';
import { getEventsBySeries } from '@/lib/services/events';
import type { EventsListProps, RsvpStatus } from '@/lib/types/events.types';
import type { Event } from '@/lib/types/database';
import { formatDateHeader, groupEventsByDate } from '@/lib/utils/events.utils';
import { Button } from '@/components/ui/button';

/**
 * Component for displaying a list of events grouped by date.
 * Fetches all user RSVPs in a single batch query to avoid N+1 API calls.
 */
export function EventsList({
  events,
  userId,
  isPast = false,
  fileCounts,
  onEventNavigate,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
}: EventsListProps) {
  const groupedEvents = useMemo(
    () => groupEventsByDate(events, isPast),
    [events, isPast]
  );

  // Extract all event IDs for batch RSVP fetching
  const eventIds = useMemo(() => events.map((e) => e.id), [events]);

  // Fetch all RSVPs for this user in a single query
  const { data: rsvpMap } = useUserRsvpsForEvents(eventIds, userId);
  const { data: queriedFileCounts } = useEventFileCounts(eventIds);
  const fileCountMap = fileCounts ?? queriedFileCounts;

  // Collect unique series IDs from events
  const seriesIds = useMemo(() => {
    const ids = new Set<string>();
    events.forEach((e) => e.series_id && ids.add(e.series_id));
    return Array.from(ids);
  }, [events]);

  // Batch fetch all series using useQueries
  const seriesQueries = useQueries({
    queries: seriesIds.map((seriesId) => ({
      queryKey: eventKeys.series(seriesId),
      queryFn: () => getEventsBySeries(seriesId),
    })),
  });

  // Build a map from eventId to seriesInfo { position, total }
  const seriesInfoMap = useMemo(() => {
    const map = new Map<number, { position: number; total: number }>();

    seriesQueries.forEach((query) => {
      const seriesEvents = query.data;
      if (seriesEvents) {
        seriesEvents.forEach((event, index) => {
          map.set(event.id, {
            position: index + 1,
            total: seriesEvents.length,
          });
        });
      }
    });

    return map;
  }, [seriesQueries]);

  // Helper to get RSVP status for an event
  const getRsvpStatus = (eventId: number): RsvpStatus => {
    const rsvp = rsvpMap?.get(eventId);
    return (rsvp?.status as RsvpStatus) || null;
  };

  if (groupedEvents.length === 0) {
    return (
      <div
        className="flex justify-center items-center py-12"
        data-testid="events-empty-state"
      >
        <p className="text-body text-foreground/60">No events found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-list">
      {groupedEvents.map((group) => (
        <div key={group.date}>
          <h2 className="text-lg font-semibold text-primary mb-3">
            {formatDateHeader(group.date)}
          </h2>
          <div className="space-y-3">
            {group.events.map((event: Event) => (
              <EventCard
                key={event.id}
                event={event}
                rsvpStatus={getRsvpStatus(event.id)}
                fileCount={fileCountMap?.get(event.id) ?? 0}
                isPast={isPast}
                onNavigate={() => onEventNavigate?.(event.id)}
                seriesInfo={seriesInfoMap.get(event.id)}
              />
            ))}
          </div>
        </div>
      ))}
      {hasNextPage && (
        <div className="flex justify-center py-4">
          <Button
            variant="link"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            data-testid="load-more-button"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}
